import React, { useEffect, useRef, useState } from 'react';
import { FiX, FiDownload, FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { BeatLoader } from 'react-spinners';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker - use local copy
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const PdfPreviewModal = ({ isOpen, onClose, pdfUrl, fileName = 'document.pdf' }) => {
    const canvasRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scale, setScale] = useState(1.5);
    const [rendering, setRendering] = useState(false);

    // Load PDF document
    useEffect(() => {
        if (!isOpen || !pdfUrl) return;

        setLoading(true);
        setError(null);

        // Fetch PDF with authentication token
        const fetchPdf = async () => {
            try {
                // Get token from localStorage
                const token = localStorage.getItem('ifex_token');

                // Fetch PDF with authentication headers
                const response = await fetch(pdfUrl, {
                    headers: {
                        'Authorization': token ? `Bearer ${token}` : '',
                    },
                    credentials: 'include', // Include cookies for Sanctum
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('Error loading PDF:', errorData);
                    throw new Error(errorData.message || `HTTP error ${response.status}`);
                }

                // Convert response to blob
                const blob = await response.blob();

                // Create blob URL
                const blobUrl = URL.createObjectURL(blob);

                // Load PDF from blob
                const loadingTask = pdfjsLib.getDocument(blobUrl);

                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
                setCurrentPage(1);
                setLoading(false);
            } catch (err) {
                console.error('Error loading PDF:', err, JSON.stringify(err));
                setError('Fehler beim Laden des PDF-Dokuments');
                setLoading(false);
            }
        };

        fetchPdf();

        // Cleanup
        return () => {
            if (pdfDoc) {
                pdfDoc.destroy();
            }
        };
    }, [isOpen, pdfUrl]);

    // Render page
    useEffect(() => {
        if (!pdfDoc || !canvasRef.current || rendering) return;

        setRendering(true);

        pdfDoc.getPage(currentPage).then((page) => {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            const viewport = page.getViewport({ scale });

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            page.render(renderContext).promise
                .then(() => {
                    setRendering(false);
                })
                .catch((err) => {
                    console.error('Error rendering page:', err);
                    setRendering(false);
                });
        });
    }, [pdfDoc, currentPage, scale, rendering]);

    // Navigation handlers
    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleZoomIn = () => {
        setScale((prevScale) => Math.min(prevScale + 0.25, 3));
    };

    const handleZoomOut = () => {
        setScale((prevScale) => Math.max(prevScale - 0.25, 0.5));
    };

    const handleDownload = async () => {
        try {
            // Get token from localStorage
            const token = localStorage.getItem('ifex_token');

            // Fetch PDF with authentication headers
            const response = await fetch(pdfUrl, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Download fehlgeschlagen');
            }

            // Convert response to blob
            const blob = await response.blob();

            // Create blob URL and trigger download
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download error:', error);
            alert('Fehler beim Herunterladen der Datei');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-6xl max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-xl">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-800">PDF Vorschau</h2>
                        <span className="text-sm text-gray-500">{fileName}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="bg-gray-50 border-b border-gray-200 p-3 flex justify-between items-center">
                    {/* Navigation */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 1 || loading}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Vorherige Seite"
                        >
                            <FiChevronLeft size={20} />
                        </button>
                        <span className="text-sm font-medium px-3">
                            Seite {currentPage} von {totalPages}
                        </span>
                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages || loading}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Nächste Seite"
                        >
                            <FiChevronRight size={20} />
                        </button>
                    </div>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleZoomOut}
                            disabled={scale <= 0.5 || loading}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Verkleinern"
                        >
                            <FiZoomOut size={20} />
                        </button>
                        <span className="text-sm font-medium px-2 min-w-[60px] text-center">
                            {Math.round(scale * 100)}%
                        </span>
                        <button
                            onClick={handleZoomIn}
                            disabled={scale >= 3 || loading}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Vergrößern"
                        >
                            <FiZoomIn size={20} />
                        </button>
                    </div>

                    {/* Download Button */}
                    <button
                        onClick={handleDownload}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-mainBlue text-white rounded hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiDownload size={18} />
                        <span>Herunterladen</span>
                    </button>
                </div>

                {/* Canvas Container */}
                <div className="flex-1 overflow-auto bg-gray-100 p-4">
                    {loading && (
                        <div className="flex flex-col items-center justify-center h-full gap-3">
                            <BeatLoader color="#3A46A9" size={15} />
                            <p className="text-gray-600">PDF wird geladen...</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center justify-center h-full gap-3">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                <FiX className="text-red-600" size={32} />
                            </div>
                            <p className="text-red-600 font-semibold">{error}</p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Schließen
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="flex justify-center">
                            <canvas
                                ref={canvasRef}
                                className="shadow-lg bg-white"
                                style={{ maxWidth: '100%', height: 'auto' }}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-white border-t border-gray-200 p-4 rounded-b-xl flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                        Schließen
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PdfPreviewModal;
